/**
 * ADSR envelope helpers. Values are computed analytically so a note-off can
 * be scheduled at a *future* time without `cancelAndHoldAtTime` (not
 * implemented in Firefox): we know the envelope value at that moment.
 */
export interface Adsr {
  attackSec: number;
  decaySec: number;
  sustain: number; // 0–1 of peak
  releaseSec: number;
}

/** Time constant used with setTargetAtTime: reaches ~98% after 4τ. */
export const timeConstant = (sec: number) => Math.max(sec, 0.001) / 4;

/** Envelope level (relative to peak) `t` seconds after note-on, while held. */
export function adsrLevelAt(env: Adsr, t: number): number {
  if (t <= 0) return 0;
  const a = Math.max(env.attackSec, 0.001);
  if (t < a) return t / a;
  const tau = timeConstant(env.decaySec);
  return env.sustain + (1 - env.sustain) * Math.exp(-(t - a) / tau);
}

/** Schedule attack/decay/sustain on a gain param. */
export function scheduleAttack(param: AudioParam, env: Adsr, peak: number, startSec: number): void {
  const a = Math.max(env.attackSec, 0.001);
  param.cancelScheduledValues(startSec);
  param.setValueAtTime(0, startSec);
  param.linearRampToValueAtTime(peak, startSec + a);
  param.setTargetAtTime(peak * env.sustain, startSec + a, timeConstant(env.decaySec));
}

/**
 * Schedule release starting at `releaseSec` for a note started at `startSec`.
 * Returns the time after which the voice is effectively silent.
 */
export function scheduleRelease(param: AudioParam, env: Adsr, peak: number, startSec: number, releaseSec: number): number {
  const level = peak * adsrLevelAt(env, releaseSec - startSec);
  param.cancelScheduledValues(releaseSec);
  param.setValueAtTime(level, releaseSec);
  param.setTargetAtTime(0, releaseSec, timeConstant(env.releaseSec));
  return releaseSec + Math.max(env.releaseSec, 0.001) * 1.5 + 0.02;
}

/** Smoothly move a param to a value (avoids zipper noise). */
export function smoothSet(param: AudioParam, value: number, ctx: BaseAudioContext, tau = 0.01): void {
  param.cancelScheduledValues(ctx.currentTime);
  param.setTargetAtTime(value, ctx.currentTime, tau);
}
